/**
 * ApprovalQueue — Tier-3 Disclosure Engine M5 / Wave 4 Agent E
 *
 * DisclosureManager-only triage surface.
 *
 * Polls GET /v3/disclosures/versions/queue (every InReview version), shows
 * one row per version with quick-action buttons:
 *   - Preview body          (read-only modal — renders mergedHtml from
 *                            existing /v3/admin/disclosures/preview route)
 *   - Approve               POST /v3/disclosures/versions/{id}/approve
 *   - Reject (with reason)  POST /v3/disclosures/versions/{id}/reject
 *
 * Approved + draft rows do NOT appear here — this is the InReview-only
 * queue. Activate happens on the existing VersionEditor page once a version
 * is in Approved status (covered by the M3 admin surface).
 *
 * Auth: Mounts only when the user holds the DisclosureManager role (route
 * gate in App.tsx). Server enforces the gate again (defense in depth).
 *
 * Bill 2026-05-09
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  Inbox,
  RefreshCw,
  XCircle,
} from 'lucide-react';

import {
  Modal,
  PillStatus,
  TextArea,
  Toast,
  useActor,
  useToast,
} from './_shared';
import { previewRender, type DisclosurePreviewResponse } from '@/services/disclosuresAdminApi';
import { getMsalToken } from '@/auth/msal';

// ─────────────────────────────────────────────────────────────────────────
// Approval-queue API surface — inline fetch wrappers (matches the pattern
// in disclosuresAdminApi.ts; kept local here because these routes live
// under /v3/disclosures/* not /v3/admin/disclosures/*).
// ─────────────────────────────────────────────────────────────────────────

interface DisclosureApprovalQueueRow {
  versionId: number;
  templateCode: string;
  templateDisplayName?: string | null;
  versionNumber: string;
  status: string;
  submittedBy?: string | null;
  submittedAt?: string | null;
  authoredBy: string;
  authoredAt: string;
  pageCount: number;
  signatureCount: number;
}

interface ApprovalQueueResponse {
  success: boolean;
  data?: { count: number; rows: ReadonlyArray<DisclosureApprovalQueueRow> };
  error?: string;
  code?: string;
}

interface TransitionResponse {
  success: boolean;
  data?: {
    versionId: number;
    templateCode: string;
    versionNumber: string;
    status: string;
  };
  error?: string;
  code?: string;
}

function getBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'https://pcb-retail-api.azurewebsites.net/api'
  );
}

async function buildHeaders(): Promise<Headers> {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-pcb-client-id': 'risk-mgmt-panel',
  });
  try {
    const token = await getMsalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    /* RoleGate already enforced auth */
  }
  return headers;
}

async function fetchQueue(): Promise<ApprovalQueueResponse> {
  const url = `${getBaseUrl()}/v3/disclosures/versions/queue`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, { method: 'GET', headers });
    const text = await resp.text();
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { raw: text };
    }
    if (!resp.ok) {
      const errMsg =
        (body && typeof body === 'object' && 'error' in body
          ? String((body as { error?: unknown }).error)
          : null) ?? `HTTP ${resp.status}`;
      return { success: false, error: errMsg };
    }
    return body as ApprovalQueueResponse;
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

async function postTransition(
  versionId: number,
  action: 'approve' | 'reject',
  body: { actor?: string; reason?: string },
): Promise<TransitionResponse> {
  const url = `${getBaseUrl()}/v3/disclosures/versions/${versionId}/${action}`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const text = await resp.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = { raw: text };
    }
    if (!resp.ok) {
      const errMsg =
        (parsed && typeof parsed === 'object' && 'error' in parsed
          ? String((parsed as { error?: unknown }).error)
          : null) ?? `HTTP ${resp.status}`;
      const errCode =
        parsed && typeof parsed === 'object' && 'code' in parsed
          ? String((parsed as { code?: unknown }).code)
          : undefined;
      return errCode !== undefined
        ? { success: false, error: errMsg, code: errCode }
        : { success: false, error: errMsg };
    }
    return parsed as TransitionResponse;
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Component.
// ─────────────────────────────────────────────────────────────────────────

interface RejectPrompt {
  versionId: number;
  templateCode: string;
  versionNumber: string;
}

interface PreviewState {
  versionId: number;
  templateCode: string;
  versionNumber: string;
  loading: boolean;
  error: string | null;
  preview: DisclosurePreviewResponse | null;
}

export function ApprovalQueue(): JSX.Element {
  const actor = useActor();
  const { toast, push, clear } = useToast();

  const [rows, setRows] = useState<ReadonlyArray<DisclosureApprovalQueueRow>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null); // versionId currently transitioning
  const [rejectPrompt, setRejectPrompt] = useState<RejectPrompt | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    const resp = await fetchQueue();
    if (resp.success && resp.data) {
      setRows(resp.data.rows ?? []);
    } else {
      setError(resp.error ?? 'Failed to load approval queue');
      setRows([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const handleApprove = useCallback(
    async (row: DisclosureApprovalQueueRow) => {
      setBusy(row.versionId);
      const result = await postTransition(row.versionId, 'approve', { actor });
      setBusy(null);
      if (result.success) {
        push('ok', `Approved ${row.templateCode} v${row.versionNumber}`);
        // Optimistic remove — Approved rows leave the InReview queue.
        setRows((prev) => prev.filter((r) => r.versionId !== row.versionId));
      } else {
        push('err', result.error ?? 'Approve failed');
      }
    },
    [actor, push],
  );

  const openRejectPrompt = useCallback((row: DisclosureApprovalQueueRow) => {
    setRejectPrompt({
      versionId: row.versionId,
      templateCode: row.templateCode,
      versionNumber: row.versionNumber,
    });
    setRejectReason('');
  }, []);

  const handleReject = useCallback(async () => {
    if (!rejectPrompt) return;
    if (!rejectReason.trim()) {
      push('err', 'Please provide a rejection reason.');
      return;
    }
    setBusy(rejectPrompt.versionId);
    const result = await postTransition(rejectPrompt.versionId, 'reject', {
      actor,
      reason: rejectReason.trim(),
    });
    setBusy(null);
    if (result.success) {
      push(
        'ok',
        `Rejected ${rejectPrompt.templateCode} v${rejectPrompt.versionNumber} — sent back to author.`,
      );
      setRows((prev) => prev.filter((r) => r.versionId !== rejectPrompt.versionId));
      setRejectPrompt(null);
      setRejectReason('');
    } else {
      push('err', result.error ?? 'Reject failed');
    }
  }, [actor, push, rejectPrompt, rejectReason]);

  const openPreview = useCallback(async (row: DisclosureApprovalQueueRow) => {
    setPreview({
      versionId: row.versionId,
      templateCode: row.templateCode,
      versionNumber: row.versionNumber,
      loading: true,
      error: null,
      preview: null,
    });
    // Render the version under review, not whichever is active (BACKLOG-1744, 2026-10-07).
    const result = await previewRender({ templateCode: row.templateCode, versionId: row.versionId });
    setPreview((prev) =>
      prev && prev.versionId === row.versionId
        ? {
            ...prev,
            loading: false,
            error: result.success ? null : result.error ?? 'Preview failed',
            preview: result.success && result.data ? result.data : null,
          }
        : prev,
    );
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  const formattedRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        submittedAtDisplay: r.submittedAt
          ? new Date(r.submittedAt).toLocaleString()
          : '—',
      })),
    [rows],
  );

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/disclosures" className="back-link">
          <ArrowLeft size={14} /> Disclosure Engine
        </Link>
        <span aria-hidden="true"> / </span>
        <span>Approval Queue</span>
      </nav>

      <section className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-header__title">
            <Inbox size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            Approval Queue
          </h1>
          <p className="page-header__subtitle page-intro">
            DisclosureManager triage. Every version currently in <code>InReview</code> status.
            Approve to move forward to <code>Approved</code> (then activate to ship), or reject
            with a reason to send the version back to <code>Draft</code> for the author to fix.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => void loadQueue()}
          disabled={loading}
          aria-label="Refresh queue"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </section>

      {error && (
        <div className="alert alert--err" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-500)' }}>
          Loading approval queue…
        </div>
      ) : formattedRows.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            border: '1px dashed var(--ink-300)',
            borderRadius: 8,
            color: 'var(--ink-500)',
          }}
        >
          <Inbox size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 500 }}>Inbox zero — nothing waiting on review.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            New submissions appear here automatically; refresh after a Risk Mgmt author submits a draft.
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Template</th>
                <th scope="col">Version</th>
                <th scope="col">Status</th>
                <th scope="col">Submitted by</th>
                <th scope="col">Submitted at</th>
                <th scope="col" style={{ textAlign: 'center' }}>Pages</th>
                <th scope="col" style={{ textAlign: 'center' }}>Signatures</th>
                <th scope="col" style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {formattedRows.map((row) => (
                <tr key={row.versionId}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {row.templateDisplayName ?? row.templateCode}
                    </div>
                    {row.templateDisplayName && (
                      <div style={{ fontSize: 11, color: 'var(--ink-500)', fontFamily: 'monospace' }}>
                        {row.templateCode}
                      </div>
                    )}
                  </td>
                  <td>
                    <Link
                      to={`/disclosures/templates/${encodeURIComponent(row.templateCode)}/versions/${encodeURIComponent(row.versionNumber)}`}
                      title="Open in version editor"
                    >
                      v{row.versionNumber}
                    </Link>
                  </td>
                  <td>
                    <PillStatus status={row.status} />
                  </td>
                  <td>{row.submittedBy ?? '—'}</td>
                  <td>{row.submittedAtDisplay}</td>
                  <td style={{ textAlign: 'center' }}>{row.pageCount}</td>
                  <td style={{ textAlign: 'center' }}>{row.signatureCount}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => void openPreview(row)}
                      disabled={busy === row.versionId}
                      title="Preview rendered body"
                    >
                      <Eye size={13} /> Preview
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => void handleApprove(row)}
                      disabled={busy === row.versionId}
                    >
                      <CheckCircle2 size={13} /> Approve
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => openRejectPrompt(row)}
                      disabled={busy === row.versionId}
                    >
                      <XCircle size={13} /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject dialog — reason required */}
      <Modal
        open={rejectPrompt !== null}
        onClose={() => {
          setRejectPrompt(null);
          setRejectReason('');
        }}
        title={
          rejectPrompt
            ? `Reject ${rejectPrompt.templateCode} v${rejectPrompt.versionNumber}?`
            : 'Reject'
        }
        width={520}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setRejectPrompt(null);
                setRejectReason('');
              }}
              disabled={busy !== null}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => void handleReject()}
              disabled={busy !== null || !rejectReason.trim()}
            >
              {busy !== null ? 'Rejecting…' : 'Send back to author'}
            </button>
          </>
        }
      >
        <p style={{ fontSize: 13, marginTop: 0 }}>
          The version returns to <code>Draft</code> status. The author sees this reason in
          the version editor and uses it to make corrections before resubmitting.
        </p>
        <label
          style={{
            display: 'block',
            fontSize: 12,
            fontWeight: 500,
            marginBottom: 4,
          }}
        >
          Rejection reason <span style={{ color: 'var(--pcb-red)' }}>*</span>
        </label>
        <TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.currentTarget.value)}
          placeholder="What needs to change? Reference specific tokens, sections, or compliance gaps."
          maxLength={512}
          autoFocus
        />
        <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>
          {rejectReason.length}/512
        </div>
      </Modal>

      {/* Preview modal — read-only render via /v3/admin/disclosures/preview */}
      <Modal
        open={preview !== null}
        onClose={closePreview}
        title={
          preview
            ? `Preview: ${preview.templateCode} v${preview.versionNumber}`
            : 'Preview'
        }
        width={920}
      >
        {preview?.loading && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-500)' }}>
            Rendering preview…
          </div>
        )}
        {preview?.error && (
          <div className="alert alert--err" role="alert">
            {preview.error}
          </div>
        )}
        {preview?.preview && (
          <div
            style={{
              border: '1px solid var(--ink-300)',
              borderRadius: 6,
              maxHeight: '60vh',
              overflow: 'auto',
              background: 'var(--surface-card)',
              padding: 16,
            }}
          >
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', fontSize: 13 }}>
              Showing version <strong>{preview.preview.versionNumber ?? '—'}</strong>
              <PillStatus status={preview.preview.versionStatus ?? null} />
              {preview.preview.isLiveVersion ? (
                <span className="pill pill--ok">Used in live packages</span>
              ) : (
                <span className="pill pill--warn">
                  Not used in live packages
                  {preview.preview.liveVersionNumber ? ` (live: version ${preview.preview.liveVersionNumber})` : ' (no active version)'}
                </span>
              )}
            </div>
            {/* Sandboxed frame (no scripts) — same pattern as VersionPreview. */}
            <iframe
              title={`Preview of ${preview.templateCode} v${preview.versionNumber}`}
              srcDoc={preview.preview.mergedHtml ?? ''}
              sandbox=""
              style={{ width: '100%', minHeight: 420, border: 'none', background: '#fff' }}
            />
            {preview.preview.diagnostics?.requiredMisses &&
              preview.preview.diagnostics.requiredMisses.length > 0 && (
                <div
                  className="alert alert--warn"
                  role="alert"
                  style={{ marginTop: 12 }}
                >
                  <strong>Required tokens unresolved in preview:</strong>{' '}
                  {preview.preview.diagnostics.requiredMisses.join(', ')}
                </div>
              )}
          </div>
        )}
      </Modal>
    </>
  );
}
